import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    Award,
    Sparkles,
    GraduationCap,
    Megaphone,
    Users as UsersIcon,
    Trophy,
    Zap,
    CheckCircle2,
    Clock,
    Coins,
} from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";

const CATEGORY_ICONS = {
    Formation: GraduationCap,
    "Simulateur IA": Sparkles,
    "Engagement social": Megaphone,
    Cooptation: UsersIcon,
    "Challenge hebdomadaire": Trophy,
};

export default function PointsRules() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/points/rules").then((r) => setData(r.data));
    }, []);

    if (!data) {
        return (
            <Layout title="Règles des points" subtitle="Comment gagner des points">
                <div className="h-32 glass-card rounded-3xl animate-pulse" />
            </Layout>
        );
    }

    return (
        <Layout title="Règles des points" subtitle="Transparence totale sur ce que tu gagnes">
            <div className="space-y-6" data-testid="points-rules-page">
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-3xl p-5 lg:p-6 border border-shariti-orange/20"
                >
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                            <Award className="w-6 h-6 text-shariti-orange" />
                        </div>
                        <div className="flex-1">
                            <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                                Règles des points
                            </p>
                            <h2 className="text-2xl lg:text-3xl font-extrabold text-white leading-tight">
                                Tu gagnes des points pour chaque action concrète.
                            </h2>
                            <p className="text-sm text-white/60 font-light mt-2 max-w-2xl leading-relaxed">
                                Les points reflètent ton engagement et ta progression. Certains
                                sont crédités automatiquement, d'autres après validation par un
                                manager (preuve par capture d'écran). Plus de zones d'ombre.
                            </p>
                        </div>
                    </div>
                    {data.cash_visible && (
                        <div
                            className="mt-4 p-3 rounded-2xl bg-shariti-yellow/10 border border-shariti-yellow/30 flex items-start gap-3"
                            data-testid="rules-cash-banner"
                        >
                            <Coins className="w-5 h-5 text-shariti-yellow shrink-0 mt-0.5" />
                            <p className="text-sm text-white/80">
                                <span className="font-bold text-shariti-yellow">Conversion en euros activée :</span>{" "}
                                1 point ={" "}
                                <span className="font-bold">
                                    {(data.cash_rate_eur_per_point).toLocaleString("fr-FR", {
                                        minimumFractionDigits: 3,
                                    })}
                                    {" "}€
                                </span>
                                . Pour {(1 / data.cash_rate_eur_per_point).toFixed(0)} points
                                accumulés, tu débloques 1 €.
                            </p>
                        </div>
                    )}
                </motion.div>

                {/* Rules by category */}
                {data.rules.map((cat, ci) => {
                    const Icon = CATEGORY_ICONS[cat.category] || Award;
                    return (
                        <motion.div
                            key={cat.category}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: ci * 0.05 }}
                            className="space-y-3"
                            data-testid={`rules-cat-${ci}`}
                        >
                            <div className="flex items-center gap-2">
                                <div className="w-9 h-9 rounded-xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                                    <Icon className="w-4 h-4 text-shariti-orange-light" />
                                </div>
                                <h3 className="text-lg font-extrabold text-white">
                                    {cat.category}
                                </h3>
                            </div>
                            <div className="space-y-2">
                                {cat.items.map((item, ii) => (
                                    <div
                                        key={ii}
                                        className="glass-card rounded-2xl p-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4"
                                        data-testid={`rules-item-${ci}-${ii}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-extrabold text-white text-sm leading-snug break-words">
                                                {item.label}
                                            </p>
                                            <p className="text-xs text-white/55 font-light mt-1 leading-relaxed">
                                                {item.details}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-start sm:items-end gap-2 shrink-0 sm:min-w-[120px]">
                                            <span className="inline-block bg-shariti-orange/20 text-shariti-orange-light text-xs sm:text-sm font-extrabold px-2.5 py-1 rounded-full whitespace-nowrap">
                                                {item.points}
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/40 whitespace-nowrap">
                                                {item.auto ? (
                                                    <>
                                                        <Zap className="w-3 h-3 shrink-0" />
                                                        Automatique
                                                    </>
                                                ) : (
                                                    <>
                                                        <Clock className="w-3 h-3 shrink-0" />
                                                        Validation requise
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    );
                })}

                <div className="text-xs text-white/40 italic flex items-center gap-2 pt-4">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    Tous les points sont traçables — clique sur ton total dans ton profil pour
                    voir l'historique détaillé.
                </div>
            </div>
        </Layout>
    );
}
